import React from 'react';

/**
 * A stored value with its name above it — NAME, PHONE, AGENDA, MODE, OWNER on
 * the Lead Details panels.
 *
 * Not to be confused with `Field`, which wraps an input somebody types into.
 * This one is read-only: tiny uppercase caption, value in medium weight below.
 * Both existed on the Sales screens; only the input one had been shared, so
 * read-only pairs kept getting hand-written a little differently each time.
 *
 * An empty value renders an em dash rather than collapsing, so a column of
 * these keeps its rhythm when one record is missing a field.
 *
 *   <DataGrid>
 *     <DataField label="Name" value="Ganesh Patil" />
 *     <DataField label="Phone" value="9822284897" numeric />
 *   </DataGrid>
 */
export const DataField = ({ label, value, numeric = false, className = '' }) => (
  <div className={`min-w-0 ${className}`}>
    <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd
      className={`mt-0.5 truncate text-sm font-semibold text-gray-900 ${
        numeric ? 'tabular-nums' : ''
      }`}
      title={typeof value === 'string' ? value : undefined}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </dd>
  </div>
);

/** Two-up by default, which is how the lead panels lay their pairs out. */
export const DataGrid = ({ children, columns = 2, className = '' }) => (
  <dl
    className={`grid gap-x-4 gap-y-3 ${
      columns === 1 ? 'grid-cols-1' : columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'
    } ${className}`}>
    {children}
  </dl>
);

/**
 * The small boxed counters at the foot of a panel — CALLS, WHATSAPP, EMAILS
 * on the Engagement card.
 *
 *   <MiniStatRow items={[{ label: 'Calls', value: 1 }, …]} />
 */
export const MiniStatRow = ({ items = [], className = '' }) => (
  <div className={`grid gap-2 ${className}`} style={{ gridTemplateColumns: `repeat(${items.length || 1}, minmax(0, 1fr))` }}>
    {items.map((s) => (
      <div
        key={s.label}
        className="rounded-control border border-hairline bg-surface-muted px-2 py-2 text-center">
        <div className="text-lg font-bold leading-none tabular-nums text-gray-900">{s.value}</div>
        <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
          {s.label}
        </div>
      </div>
    ))}
  </div>
);

export default DataField;
